import { BIOME_BY_THEME_ID, specialItemsForBiome, type BiomeId, type CollectState, type Rarity } from '@goodtime/shared';

/**
 * Reel collectibles (`reel_collect`, on every rr_v1 slot): a slot's world
 * has three relics (a common, a rare and its legendary). They turn up on
 * the reels now and then; finding all three pays the set's prize, and the
 * set starts again. Reaching gold on a slot lands one too, once. The server
 * decides every find and pays the prize; this is what the app shows and
 * says about them.
 */

/** What players call them, in one place. */
export const RELICS_NAME = 'Relics';

/** The one-tap explanation, on the relics board. */
export const RELICS_EXPLAINER =
  'Relics turn up on the reels now and then. Find all three for the prize, then hunt for the set again.';

/** The title card when the third relic lands. */
export const SET_TITLE = 'ALL THREE RELICS!';

/** The title card before the relic a slot gives once for reaching gold
 *  (its third star), and the line under it. */
export const GOLD_TITLE = 'GOLD!';
export const GOLD_LINE = 'A relic for mastering this slot';

/** One of a world's relics. */
export interface Relic {
  id: string;
  name: string;
  glyph: string;
  rarity: Rarity;
  /** Its painting's texture alias (the reel symbol's). */
  art: string;
}

/** The worlds' first slots, which aren't in the themed-slot registry. */
const HERO_THEMES: Readonly<Record<string, BiomeId>> = {
  forest: 'kingdom',
  underwater: 'cove',
  frost: 'frost',
  frontier: 'frontier',
  neon: 'neon',
};

/** A slot's three relics in set order (common, rare, legendary), or none
 *  when its world has no relics. */
export function relicsFor(themeId: string): Relic[] {
  const biome = BIOME_BY_THEME_ID[themeId] ?? HERO_THEMES[themeId];
  if (!biome) return [];
  return specialItemsForBiome(biome).map((s) => ({
    id: s.id,
    name: s.name,
    glyph: s.glyph,
    rarity: s.rarity,
    art: `shared:reel:artefact_${s.id}`,
  }));
}

/** The reel symbol id a found relic lands as. */
export function relicSymbolId(relic: Relic): string {
  return `artefact_${relic.id}`;
}

const NUMBER = new Intl.NumberFormat('en-GB');

/** "500 coins", "1,000 coins", "10 gems", "1 gem". */
export function prizeText(prize: { coins: number; gems: number }): string {
  if (prize.gems > 0 && prize.coins <= 0) return `${NUMBER.format(prize.gems)} gem${prize.gems === 1 ? '' : 's'}`;
  return `${NUMBER.format(prize.coins)} coin${prize.coins === 1 ? '' : 's'}`;
}

/** How many of the set are held. */
export function heldCount(state: Pick<CollectState, 'pieces'>): number {
  return state.pieces.filter(Boolean).length;
}

const WORDS = ['No more', 'One more', 'Two more', 'Three more'];

/** Under a find (the first or the second): how far along the set is. */
export function findLine(state: Pick<CollectState, 'pieces' | 'prize'>): string {
  const held = heldCount(state);
  const left = state.pieces.length - held;
  const more = WORDS[left] ?? `${left} more`;
  return `${held} of ${state.pieces.length} found. ${more} for ${prizeText(state.prize)}${left === 1 ? '!' : '.'}`;
}

/** The relics board's lines: the prize for this set, and the sets so far. */
export function boardLines(state: Pick<CollectState, 'prize' | 'sets'>): { prize: string; sets: string } {
  return {
    prize: `Find all three: ${prizeText(state.prize)}`,
    sets: state.sets === 0 ? 'No sets found here yet' : `Sets found here: ${NUMBER.format(state.sets)}`,
  };
}
